import React from 'react';
import type { OrderTableColumn, OrderColumnKey } from '@hero/hooks-shared';

/** Relative weights — normalized to fill 100% for whichever columns are visible. */
const COLUMN_WEIGHT: Partial<Record<OrderColumnKey, number>> = {
  ref: 14,
  destinations: 18,
  status: 13,
  driver: 20,
  price: 12,
  actions: 10,
  date: 12,
  podRef: 10,
  sender: 12,
  receiver: 12,
  customer: 12,
  payment: 11,
  distance: 10,
};

const CHECKBOX_WEIGHT = 4;

/**
 * `<colgroup>` for the order table. Percentages always sum to 100% for the
 * visible columns (+ checkbox when row selection is on), so the table fills
 * edge-to-edge without dead zones at the start, middle, or end.
 */
export function OrderTableColGroup({
  columns,
  hasCheckbox,
}: {
  columns: OrderTableColumn[];
  hasCheckbox: boolean;
}): React.ReactElement {
  const checkboxPct = hasCheckbox ? CHECKBOX_WEIGHT : 0;
  const available = 100 - checkboxPct;
  const weightSum = columns.reduce(
    (sum, col) => sum + (COLUMN_WEIGHT[col.key] ?? 10),
    0,
  );

  return (
    <colgroup>
      {hasCheckbox ? <col style={{ width: `${checkboxPct}%` }} /> : null}
      {columns.map(col => {
        const weight = COLUMN_WEIGHT[col.key] ?? 10;
        const pct = (weight / weightSum) * available;
        return <col key={col.key} style={{ width: `${pct}%` }} />;
      })}
    </colgroup>
  );
}
